/**
 * LiveDocsTable.jsx — Real-time enrichment table with all SAGD columns.
 * Supports pagination for up to 5,000 rows.
 */

import { memo, useState, useEffect, useRef } from "react";

const PAGE_SIZES = [25, 50, 100];

const COLS = [
  { key: "doc_no",        label: "#",              width: 52,  align: "center" },
  { key: "status_badge",  label: "Status",         width: 88,  align: "center" },
  { key: "date_added",    label: "Date Added",     width: 90 },
  { key: "document_type", label: "Doc Type",       width: 110 },
  { key: "question_bank", label: "Question Bank",  width: 160 },
  { key: "trl",           label: "TRL",            width: 52,  align: "center" },
  { key: "title",         label: "Title",          width: 220 },
  { key: "pub_date",      label: "Pub. Date",      width: 86 },
  { key: "summary",       label: "Summary",        width: 260 },
  { key: "category",      label: "Primary Cat.",   width: 130 },
  { key: "secondary_cat", label: "Secondary Cat.", width: 130 },
  { key: "relevance",     label: "Relevance",      width: 80,  align: "center" },
  { key: "subtopics",     label: "Subtopics",      width: 150 },
  { key: "companies",     label: "Companies",      width: 140 },
  { key: "use_cases",     label: "Use Cases",      width: 160 },
  { key: "grant",         label: "Grant/Funding",  width: 100 },
  { key: "bd",            label: "BD Priority",    width: 88,  align: "center" },
  { key: "sourcing",      label: "Sourcing Pri.",  width: 88,  align: "center" },
  { key: "tech",          label: "Tech Theme",     width: 130 },
  { key: "operators",     label: "Operators",      width: 140 },
  { key: "trusted",       label: "Trusted",        width: 65,  align: "center" },
  { key: "url",           label: "URL",            width: 180 },
];

export default memo(function LiveDocsTable({ items = [], total = 0 }) {
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [jumpVal,  setJumpVal]  = useState("");
  const tableTopRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  // Reset to page 1 when items list resets (pipeline restarted)
  useEffect(() => {
    if (items.length === 0) setPage(1);
  }, [items.length]);

  // Clamp page if total pages shrinks
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIdx = (page - 1) * pageSize;
  const shown    = items.slice(startIdx, startIdx + pageSize);
  const endIdx   = Math.min(startIdx + pageSize, items.length);

  function goTo(p) {
    const clamped = Math.max(1, Math.min(p, totalPages));
    setPage(clamped);
    tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  function handleJump(e) {
    e.preventDefault();
    const p = parseInt(jumpVal, 10);
    if (!isNaN(p)) { goTo(p); setJumpVal(""); }
  }

  function handlePageSize(size) {
    const firstItem = startIdx;          // keep the same first-row visible
    setPageSize(size);
    setPage(Math.floor(firstItem / size) + 1);
  }

  // Page number buttons — show at most 7 around current
  const pageButtons = buildPageButtons(page, totalPages);

  return (
    <section className="hm-section">
      <div className="hm-section-header">
        <h2 className="hm-section-title">Live Documents</h2>
        <span className="hm-section-badge">
          {items.length.toLocaleString()} / {total.toLocaleString()}
        </span>
        {items.length === 0 && (
          <span style={{ marginLeft: 12, fontSize: 12, color: "var(--text-tertiary)" }}>
            Rows appear as documents are scraped &amp; enriched
          </span>
        )}
        {/* Rows per page selector — right-aligned */}
        {items.length > 0 && (
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span style={{ color: "var(--text-tertiary)" }}>Rows/page:</span>
            {PAGE_SIZES.map((s) => (
              <button key={s} onClick={() => handlePageSize(s)}
                style={{
                  padding: "2px 8px", fontSize: 11, fontWeight: 600,
                  border: "1px solid var(--divider-color)",
                  borderRadius: 4,
                  background: pageSize === s ? "var(--category-color-focus)" : "var(--surface-secondary)",
                  color: pageSize === s ? "#fff" : "var(--text-secondary)",
                  cursor: "pointer",
                }}>
                {s}
              </button>
            ))}
          </span>
        )}
      </div>

      {/* Scroll anchor */}
      <div ref={tableTopRef} />

      <div className="hm-table-wrap">
        <table className="hm-table">
          <thead>
            <tr>
              {COLS.map((c) => (
                <th key={c.key}
                  style={{ minWidth: c.width, width: c.width, textAlign: c.align || "left" }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={COLS.length}
                  style={{ textAlign: "center", padding: "40px 0", color: "var(--text-tertiary)" }}>
                  No documents yet — start the pipeline to see live data
                </td>
              </tr>
            ) : (
              shown.map((row) => <DocRow key={row.doc_no} row={row} />)
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination bar */}
      {items.length > pageSize && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 4px 4px",
          flexWrap: "wrap",
        }}>
          {/* Row range info */}
          <span style={{ fontSize: 12, color: "var(--text-tertiary)", marginRight: 4 }}>
            {(startIdx + 1).toLocaleString()}–{endIdx.toLocaleString()} of {items.length.toLocaleString()}
          </span>

          {/* First / Prev */}
          <PagBtn onClick={() => goTo(1)} disabled={page === 1} label="«" title="First page" />
          <PagBtn onClick={() => goTo(page - 1)} disabled={page === 1} label="‹ Prev" />

          {/* Page number buttons */}
          {pageButtons.map((b, i) =>
            b === "…" ? (
              <span key={i} style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "0 2px" }}>…</span>
            ) : (
              <PagBtn key={b} onClick={() => goTo(b)} label={b}
                active={b === page} />
            )
          )}

          {/* Next / Last */}
          <PagBtn onClick={() => goTo(page + 1)} disabled={page === totalPages} label="Next ›" />
          <PagBtn onClick={() => goTo(totalPages)} disabled={page === totalPages} label="»" title="Last page" />

          {/* Jump to page */}
          {totalPages > 10 && (
            <form onSubmit={handleJump}
              style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8 }}>
              <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Go to:</span>
              <input
                type="number" min={1} max={totalPages}
                value={jumpVal}
                onChange={(e) => setJumpVal(e.target.value)}
                style={{
                  width: 52, padding: "2px 6px", fontSize: 12,
                  border: "1px solid var(--divider-color)",
                  borderRadius: 4,
                  background: "var(--surface-secondary)",
                  color: "var(--text-primary)",
                }}
              />
              <button type="submit" style={{
                padding: "2px 8px", fontSize: 11, fontWeight: 600,
                border: "1px solid var(--divider-color)", borderRadius: 4,
                background: "var(--surface-secondary)", color: "var(--text-secondary)",
                cursor: "pointer",
              }}>Go</button>
            </form>
          )}

          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-tertiary)" }}>
            Page {page} of {totalPages.toLocaleString()}
          </span>
        </div>
      )}
    </section>
  );
});

// ── Pagination helpers ─────────────────────────────────────────────

function PagBtn({ onClick, disabled, label, title, active }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      style={{
        padding: "3px 9px", fontSize: 12, fontWeight: active ? 700 : 500,
        border: "1px solid var(--divider-color)",
        borderRadius: 4,
        background: active ? "var(--category-color-focus)" : "var(--surface-secondary)",
        color: active ? "#fff" : disabled ? "var(--text-tertiary)" : "var(--text-secondary)",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}>
      {label}
    </button>
  );
}

function buildPageButtons(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  // Always show first, last, current, and 2 around current
  const around = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  const sorted = [...around].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push("…");
    pages.push(sorted[i]);
  }
  return pages;
}

const DocRow = memo(function DocRow({ row }) {
  const enriched = !!row.enrichment_done;
  const scored   = !!row.scoring_done;

  let statusClass = "hm-status-dot--idle";
  let statusLabel = "Scraped";
  if (scored)        { statusClass = "hm-status-dot--done";    statusLabel = "Scored"; }
  else if (enriched) { statusClass = "hm-status-dot--running"; statusLabel = "Enriched"; }

  return (
    <tr className="hm-table__row-animate">
      <td style={{ textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>{row.doc_no}</td>
      <td style={{ textAlign: "center" }}>
        <span className={`hm-status-pill ${statusClass}`}>{statusLabel}</span>
      </td>
      <td><Txt val={row.date_added} dim /></td>
      <td><Pill val={row.document_type} color="#6366F1" /></td>
      <td><Txt val={row.question_bank} clamp /></td>
      <td style={{ textAlign: "center" }}>
        <Txt val={row.trl != null && row.trl !== "" ? String(row.trl) : null} dim />
      </td>
      <td><Txt val={row.title} bold /></td>
      <td><Txt val={row.pub_date} dim /></td>
      <td><Txt val={row.summary} clamp /></td>
      <td><Pill val={row.category} color="#1E90FF" /></td>
      <td><Pill val={row.secondary_cat} color="#0E6BA8" /></td>
      <td style={{ textAlign: "center" }}><ScorePill val={row.relevance} /></td>
      <td><TagList val={row.subtopics} /></td>
      <td><TagList val={row.companies} /></td>
      <td><Txt val={row.use_cases} clamp /></td>
      <td><Txt val={row.grant} dim /></td>
      <td style={{ textAlign: "center" }}><ScorePill val={row.bd} /></td>
      <td style={{ textAlign: "center" }}><ScorePill val={row.sourcing} /></td>
      <td><Pill val={row.tech} color="#DB2777" /></td>
      <td><TagList val={row.operators} /></td>
      <td style={{ textAlign: "center" }}><TrustedBadge val={row.trusted} /></td>
      <td>
        {row.url
          ? <a href={row.url} target="_blank" rel="noreferrer" className="hm-link"
               style={{ fontSize: 11, wordBreak: "break-all" }}>
              {truncUrl(row.url)}
            </a>
          : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
      </td>
    </tr>
  );
});

// ── Cell helpers ──────────────────────────────────────────────────

function Txt({ val, bold, dim, clamp }) {
  if (!val || val === "N/A") return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const style = {
    fontWeight: bold ? 600 : undefined,
    color: dim ? "var(--text-tertiary)" : undefined,
    display: clamp ? "-webkit-box" : undefined,
    WebkitLineClamp: clamp ? 2 : undefined,
    WebkitBoxOrient: clamp ? "vertical" : undefined,
    overflow: clamp ? "hidden" : undefined,
    fontSize: 12,
    lineHeight: 1.4,
  };
  return <span style={style}>{val}</span>;
}

function Pill({ val, color }) {
  if (!val || val === "N/A") return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  return (
    <span style={{
      display: "inline-block",
      background: color + "1A",
      color,
      border: `1px solid ${color}33`,
      borderRadius: 4,
      padding: "1px 6px",
      fontSize: 11,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 120,
    }}>
      {val}
    </span>
  );
}

function TagList({ val }) {
  if (!val || val === "N/A") return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const items = Array.isArray(val)
    ? val
    : String(val).split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  if (!items.length) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const show = items.slice(0, 3);
  const rest  = items.length - show.length;
  return (
    <span style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
      {show.map((t, i) => (
        <span key={i} style={{
          background: "var(--surface-secondary)",
          border: "1px solid var(--divider-color-light)",
          borderRadius: 3,
          padding: "1px 5px",
          fontSize: 10,
          color: "var(--text-secondary)",
          whiteSpace: "nowrap",
        }}>
          {t}
        </span>
      ))}
      {rest > 0 && (
        <span style={{ fontSize: 10, color: "var(--text-tertiary)", padding: "1px 4px" }}>+{rest}</span>
      )}
    </span>
  );
}

function TrustedBadge({ val }) {
  if (val == null || val === "" || val === "N/A")
    return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const yes = String(val).toLowerCase() === "yes";
  return (
    <span style={{
      fontWeight: 700, fontSize: 11,
      color: yes ? "#0F7E6D" : "#9CA3AF",
      background: yes ? "#0F7E6D1A" : "var(--surface-secondary)",
      border: `1px solid ${yes ? "#0F7E6D33" : "var(--divider-color-light)"}`,
      borderRadius: 3,
      padding: "1px 6px",
    }}>
      {yes ? "✓" : "—"}
    </span>
  );
}

const SCORE_COLORS = { high: "#DB2777", medium: "#F59E0B", low: "#6B7280" };
function ScorePill({ val }) {
  if (!val || val === "N/A") return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const s = String(val).toLowerCase();
  const color = s.includes("high") ? SCORE_COLORS.high
              : s.includes("med")  ? SCORE_COLORS.medium
              : SCORE_COLORS.low;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600,
      color,
      background: color + "1A",
      border: `1px solid ${color}33`,
      borderRadius: 3,
      padding: "1px 5px",
    }}>
      {val}
    </span>
  );
}

function truncUrl(url) {
  try {
    const u = new URL(url);
    return u.hostname + u.pathname.slice(0, 28);
  } catch {
    return url.slice(0, 40);
  }
}
